import React from 'react';
import {Box, Typography, Grid} from '@mui/material';

const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const TEAL   = '#1B6B55';

/* ── KPI card ───────────────────────────────────────────────────── */
const KpiCard = ({label, value, subtitle}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderTop: `4px solid ${ORANGE}`,
        borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
    }}>
        {/* Small-caps label */}
        <Typography sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: ORANGE,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
        }}>
            {label}
        </Typography>

        {/* Big value */}
        <Typography sx={{
            fontSize: '52px',
            fontWeight: 800,
            color: NAVY,
            lineHeight: 1.1,
            mt: 0.5,
        }}>
            {value}
        </Typography>

        {/* Subtitle */}
        <Typography sx={{fontSize: '13px', color: '#6A7282', mb: 1.5}}>
            {subtitle}
        </Typography>

        {/* Outcome button */}
        <Box sx={{
            backgroundColor: NAVY,
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 700,
            textAlign: 'center',
            py: 1,
            borderRadius: '8px',
            mt: 'auto',
        }}>
            Outcome
        </Box>
    </Box>
);

const KPI_CARDS = [
    {label: 'Acquisition',        value: '66%',   subtitle: 'reached target level'},
    {label: 'Improved Learners',  value: '57%',   subtitle: 'score rose meaningfully'},
    {label: 'Effect Size',        value: '0.42',  subtitle: 'standardised pre/post'},
    {label: 'Modules Passed',     value: '398k',  subtitle: 'of 412k assessed'},
];

/* ── Acquisition rate bars ──────────────────────────────────────── */
const ACQ_LEFT  = [
    {country: 'United Kingdom', pct: 72},
    {country: 'Australia',      pct: 70},
    {country: 'India',          pct: 67},
];
const ACQ_RIGHT = [
    {country: 'Ireland',        pct: 64},
    {country: 'Kenya',          pct: 59},
    {country: 'Philippines',    pct: 52},
];

const AcqBar = ({pct}) => (
    <Box sx={{flex: 1, height: '22px', borderRadius: '4px', backgroundColor: 'var(--brand-surface-subtle, #E5E7EB)', overflow: 'hidden'}}>
        <Box sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: NAVY,
            borderRadius: '4px',
            transition: 'width 0.4s ease',
        }}/>
    </Box>
);

const AcqRow = ({country, pct}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Typography sx={{fontSize: '14px', color: '#374151', minWidth: '140px', flexShrink: 0}}>
            {country}
        </Typography>
        <AcqBar pct={pct}/>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, minWidth: '42px', textAlign: 'right', flexShrink: 0}}>
            {pct}%
        </Typography>
    </Box>
);

const AcquisitionTab = () => (
    <Box>
        {/* KPI cards row */}
        <Grid container spacing={2.5} sx={{mb: 3}}>
            {KPI_CARDS.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.label}>
                    <KpiCard {...card}/>
                </Grid>
            ))}
        </Grid>

        {/* Acquisition rate by country */}
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '12px',
            backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            p: 3,
        }}>
            <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 3}}>
                Acquisition rate by country (% reaching target competency)
            </Typography>

            {/* 2-column bar grid */}
            <Box sx={{display: 'flex', gap: 5}}>
                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 2.2}}>
                    {ACQ_LEFT.map((row) => <AcqRow key={row.country} {...row}/>)}
                </Box>
                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 2.2}}>
                    {ACQ_RIGHT.map((row) => <AcqRow key={row.country} {...row}/>)}
                </Box>
            </Box>
        </Box>

        {/* Decision guidance footer */}
        <Box sx={{
            mt: 3,
            p: {xs: 2, md: 2.5},
            backgroundColor: 'var(--brand-surface-subtle, #EFF6FF)',
            border: '1px solid #BFDBFE',
            borderLeft: `4px solid ${NAVY}`,
            borderRadius: '0 8px 8px 0',
        }}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                <strong style={{color: NAVY}}>Decision guidance:&nbsp;</strong>
                Acquisition data is the most reused efficacy engine. Lead renewals with effect size and
                acquisition rate; treat the lower-acquisition markets (Kenya, Philippines) as support
                priorities, not failures.
            </Typography>
        </Box>
    </Box>
);

export default AcquisitionTab;
